/**
 * Reports API service
 * Handles calls to the four report endpoints
 */

import { httpClient } from '@/utils';

export type ReportPeriod = 'week' | 'month' | 'quarter' | 'year';
export type BackendReportPeriod = 'this_week' | 'this_month' | 'this_quarter' | 'this_year';

export interface ReportsQueryOptions {
    period?: ReportPeriod;
    referenceDate?: string;
}

const mapPeriodToBackend = (period?: ReportPeriod): BackendReportPeriod | undefined => {
    const map: Record<ReportPeriod, BackendReportPeriod> = {
        week: 'this_week',
        month: 'this_month',
        quarter: 'this_quarter',
        year: 'this_year',
    };

    return period ? map[period] : undefined;
};

const buildQuery = (options?: ReportsQueryOptions): string => {
    const params = new URLSearchParams();

    const mappedPeriod = mapPeriodToBackend(options?.period);
    if (mappedPeriod) {
        params.set('period', mappedPeriod);
    }

    if (options?.referenceDate) {
        params.set('reference_date', options.referenceDate);
    }

    const query = params.toString();
    return query ? `?${query}` : '';
};

// ────────────────────────────────────────────────────────────────────────────
// Technicians report  →  GET /reports/technicians/
// ────────────────────────────────────────────────────────────────────────────
export interface APIReportTechnicianRow {
    name: string;
    services: number;
    hours: number;
    efficiency: number;
    revenue: number;
    incomePerService?: number;
    [key: string]: unknown;
}

export type APIReportTechnicians = APIReportTechnicianRow[];

// ────────────────────────────────────────────────────────────────────────────
// Costs report  →  GET /reports/costs/
// ────────────────────────────────────────────────────────────────────────────
export interface APIReportCostClientRow {
    name: string;
    services: number;
    materials: number;
    labor: number;
    total: number;
    [key: string]: unknown;
}

export interface APIReportCostPestRow {
    pest: string;
    occurrences: number;
    materials: number;
    labor: number;
    total: number;
    [key: string]: unknown;
}

export interface APIReportCosts {
    byClient: APIReportCostClientRow[];
    byPest?: APIReportCostPestRow[];
    totals?: {
        total_work_orders?: number;
        total_material_cost?: number;
    };
    [key: string]: unknown;
}

// ────────────────────────────────────────────────────────────────────────────
// Material consumption report  →  GET /reports/material-consumption/
// ────────────────────────────────────────────────────────────────────────────
export interface APIReportMaterialRow {
    material: string;
    quantity: number;
    unit: string;
    cost: number;
    services: number;
    [key: string]: unknown;
}

export type APIReportMaterialConsumption = APIReportMaterialRow[];

// ────────────────────────────────────────────────────────────────────────────
// KPIs report  →  GET /reports/kpis/
// ────────────────────────────────────────────────────────────────────────────
export interface APIReportKPIs {
    servicesCompleted: number;
    avgResponseTime: number;
    technicianEfficiency: number;
    materialUtilization: number;
    revenuePerService: number;
    costPerService: number;
    profitMargin: number;
    customerSatisfaction?: number;
    [key: string]: unknown;
}

export interface APIReportDashboardKPIs {
    activeServices: number;
    pendingQuotes: number;
    stockAlerts: number;
    monthlyRevenue: number;
    [key: string]: unknown;
}

export interface APIReportDashboard {
    kpis: APIReportDashboardKPIs;
    [key: string]: unknown;
}

interface BackendTechnicianResponse {
    items?: Array<{
        technician?: { id: number; name: string };
        completed_services?: number;
        worked_hours?: number;
        efficiency?: number;
        generated_income?: number;
        income_per_service?: number;
    }>;
}

interface BackendCostsResponse {
    items?: Array<{
        client?: { id: number; name: string };
        total_work_orders?: number;
        total_material_cost?: number;
    }>;
    totals?: {
        total_work_orders?: number;
        total_material_cost?: number;
    };
}

interface BackendKPIsResponse {
    kpis?: {
        total_completed_orders?: number;
        technician_efficiency?: number;
        average_cost_per_order?: number;
        average_response_time_hours?: number;
        material_utilization_percentage?: number;
        profit_margin?: number;
        average_income_per_work_order?: number;
    };
}

interface BackendDashboardResponse {
    kpis?: {
        total_work_orders_in_progress?: number;
        total_pending_budgets?: number;
        total_low_stock_materials?: number;
        average_monthly_income?: number;
        active_services?: number;
        pending_quotes?: number;
        stock_alerts?: number;
        monthly_revenue?: number;
    };
    total_work_orders_in_progress?: number;
    total_pending_budgets?: number;
    total_low_stock_materials?: number;
    average_monthly_income?: number;
    active_services?: number;
    pending_quotes?: number;
    stock_alerts?: number;
    monthly_revenue?: number;
}

const normalizeTechnicians = (
    payload: APIReportTechnicians | BackendTechnicianResponse,
): APIReportTechnicians => {
    if (Array.isArray(payload)) return payload;

    return (payload.items ?? []).map((item) => ({
        name: item.technician?.name ?? 'Técnico',
        services: item.completed_services ?? 0,
        hours: item.worked_hours ?? 0,
        efficiency: item.efficiency ?? 0,
        revenue: item.generated_income ?? 0,
        incomePerService: item.income_per_service ?? 0,
    }));
};

const normalizeCosts = (
    payload: APIReportCosts | BackendCostsResponse,
): APIReportCosts => {
    if ('byClient' in payload || 'byPest' in payload) return payload as APIReportCosts;

    const response = payload as BackendCostsResponse;
    return {
        byClient: (response.items ?? []).map((item) => ({
            name: item.client?.name ?? 'Cliente',
            services: item.total_work_orders ?? 0,
            materials: item.total_material_cost ?? 0,
            labor: 0,
            total: item.total_material_cost ?? 0,
        })),
        byPest: [],
        totals: response.totals,
    };
};

const normalizeMaterials = (
    payload:
        | APIReportMaterialConsumption
        | { items?: Array<{ material?: { name?: string } | string; quantity?: number; unit?: string; total_cost?: number; related_services?: number }> },
): APIReportMaterialConsumption => {
    if (Array.isArray(payload)) return payload;

    return (payload.items ?? []).map((item) => ({
        material:
            typeof item.material === 'string'
                ? item.material
                : (item.material?.name ?? 'Material'),
        quantity: item.quantity ?? 0,
        unit: item.unit ?? 'ud',
        cost: item.total_cost ?? 0,
        services: item.related_services ?? 0,
    }));
};

const normalizeKPIs = (
    payload: APIReportKPIs | BackendKPIsResponse,
): APIReportKPIs => {
    if ('servicesCompleted' in payload) return payload as APIReportKPIs;

    const kpis = (payload as BackendKPIsResponse).kpis ?? {};
    return {
        servicesCompleted: kpis.total_completed_orders ?? 0,
        avgResponseTime: kpis.average_response_time_hours ?? 0,
        technicianEfficiency: kpis.technician_efficiency ?? 0,
        materialUtilization: kpis.material_utilization_percentage ?? 0,
        revenuePerService: kpis.average_income_per_work_order ?? 0,
        costPerService: kpis.average_cost_per_order ?? 0,
        profitMargin: kpis.profit_margin ?? 0,
        customerSatisfaction: 0,
    };
};

const normalizeDashboard = (
    payload: APIReportDashboard | BackendDashboardResponse,
): APIReportDashboard => {
    if ('kpis' in payload && payload.kpis && 'activeServices' in payload.kpis) {
        return payload as APIReportDashboard;
    }

    const backendPayload = payload as BackendDashboardResponse;
    const backendKpis = backendPayload.kpis;

    return {
        kpis: {
            activeServices:
                backendKpis?.total_work_orders_in_progress ??
                backendPayload.total_work_orders_in_progress ??
                backendKpis?.active_services ??
                backendPayload.active_services ??
                0,
            pendingQuotes:
                backendKpis?.total_pending_budgets ??
                backendPayload.total_pending_budgets ??
                backendKpis?.pending_quotes ??
                backendPayload.pending_quotes ??
                0,
            stockAlerts:
                backendKpis?.total_low_stock_materials ??
                backendPayload.total_low_stock_materials ??
                backendKpis?.stock_alerts ??
                backendPayload.stock_alerts ??
                0,
            monthlyRevenue:
                backendKpis?.average_monthly_income ??
                backendPayload.average_monthly_income ??
                backendKpis?.monthly_revenue ??
                backendPayload.monthly_revenue ??
                0,
        },
    };
};

// ────────────────────────────────────────────────────────────────────────────
// Service
// ────────────────────────────────────────────────────────────────────────────
export const reportsService = {
    getDashboard(period?: ReportPeriod, referenceDate?: string): Promise<APIReportDashboard> {
        const query = buildQuery({ period, referenceDate });
        return httpClient
            .get<APIReportDashboard | BackendDashboardResponse>(`/reports/dashboard/${query}`)
            .then(normalizeDashboard);
    },

    getTechnicians(period?: ReportPeriod, referenceDate?: string): Promise<APIReportTechnicians> {
        const query = buildQuery({ period, referenceDate });
        return httpClient
            .get<APIReportTechnicians | BackendTechnicianResponse>(`/reports/technicians/${query}`)
            .then(normalizeTechnicians);
    },

    getCosts(period?: ReportPeriod, referenceDate?: string): Promise<APIReportCosts> {
        const query = buildQuery({ period, referenceDate });
        return httpClient
            .get<APIReportCosts | BackendCostsResponse>(`/reports/costs/${query}`)
            .then(normalizeCosts);
    },

    getMaterialConsumption(period?: ReportPeriod, referenceDate?: string): Promise<APIReportMaterialConsumption> {
        const query = buildQuery({ period, referenceDate });
        return httpClient
            .get<
                APIReportMaterialConsumption | { items?: Array<{ material?: { name?: string } | string; quantity?: number; unit?: string; total_cost?: number; related_services?: number }> }
            >(`/reports/material-consumption/${query}`)
            .then(normalizeMaterials);
    },

    getKPIs(period?: ReportPeriod, referenceDate?: string): Promise<APIReportKPIs> {
        const query = buildQuery({ period, referenceDate });
        return httpClient
            .get<APIReportKPIs | BackendKPIsResponse>(`/reports/kpis/${query}`)
            .then(normalizeKPIs);
    },
};
